import { z } from 'zod';
import { currencyCodeSchema, idSchema, isoDateTimeSchema, moneySchema, percentSchema, } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { compareDecimal } from '../utils/decimal.js';
export const fiscalFxMethodSchema = z.enum(['MONTHLY_BMF', 'APPROVED_DAILY']);
export const fiscalFxDirectionSchema = z.enum(['FOREIGN_PER_EUR', 'EUR_PER_FOREIGN']);
export const fiscalFxQuoteSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,12})?$/u)
    .refine((value) => /^\d{1,12}(\.\d{1,12})?$/u.test(value) && compareDecimal(value, '0') > 0, 'fiscalFx.errors.positiveQuote');
const evidence = z.string().trim().min(20).max(20000);
const rateFields = {
    sourceCurrency: currencyCodeSchema.refine((value) => value !== 'EUR'),
    method: fiscalFxMethodSchema,
    quoteDirection: fiscalFxDirectionSchema,
    quote: fiscalFxQuoteSchema,
    validFrom: ledgerDateSchema,
    validTo: ledgerDateSchema,
    sourceUrl: z
        .string()
        .url()
        .max(2000)
        .refine((value) => value.startsWith('https://'), 'fiscalFx.errors.officialSource'),
    sourceEvidence: evidence,
    sourcePublishedOn: ledgerDateSchema,
    approvalReference: z.string().trim().min(3).max(200).nullable(),
    approvalEvidence: evidence.nullable(),
    approvalValidFrom: ledgerDateSchema.nullable(),
    approvalValidTo: ledgerDateSchema.nullable(),
};
export const fiscalFxRateInputSchema = z
    .object({
    ...rateFields,
    supersedesId: idSchema.nullable().default(null),
    idempotencyKey: z.string().trim().min(1).max(160),
})
    .superRefine((value, context) => {
    const issue = (path, message) => context.addIssue({ code: 'custom', path: [path], message });
    if (value.validFrom > value.validTo)
        issue('validTo', 'fiscalFx.errors.dateRange');
    if (value.method === 'MONTHLY_BMF') {
        const next = new Date(`${value.validFrom}T00:00:00.000Z`);
        if (Number.isNaN(next.valueOf())) {
            issue('validFrom', 'fiscalFx.errors.dateRange');
            return;
        }
        next.setUTCMonth(next.getUTCMonth() + 1, 0);
        if (!value.validFrom.endsWith('-01') || value.validTo !== next.toISOString().slice(0, 10))
            issue('validTo', 'fiscalFx.errors.month');
        if (!z.string().url().safeParse(value.sourceUrl).success) {
            issue('sourceUrl', 'fiscalFx.errors.officialSource');
            return;
        }
        if (!/^https:\/\/([a-z0-9-]+\.)*bundesfinanzministerium\.de\//iu.test(value.sourceUrl))
            issue('sourceUrl', 'fiscalFx.errors.officialSource');
        if (value.approvalReference ||
            value.approvalEvidence ||
            value.approvalValidFrom ||
            value.approvalValidTo)
            issue('approvalReference', 'fiscalFx.errors.policy');
    }
    else {
        if (value.validFrom !== value.validTo)
            issue('validTo', 'fiscalFx.errors.day');
        if (!value.approvalReference ||
            !value.approvalEvidence ||
            !value.approvalValidFrom ||
            !value.approvalValidTo ||
            value.approvalValidFrom > value.validFrom ||
            value.approvalValidTo < value.validTo)
            issue('approvalReference', 'fiscalFx.errors.authorityApproval');
    }
});
export const fiscalFxRateSchema = z.object({
    ...rateFields,
    id: idSchema,
    supersedesId: idSchema.nullable(),
    version: z.number().int().positive(),
    evidenceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    reviewedAt: isoDateTimeSchema,
    reviewedById: idSchema,
    createdAt: isoDateTimeSchema,
});
export const fiscalFxRateListSchema = z.object({ data: z.array(fiscalFxRateSchema) });
export const fiscalFxProofSchema = z.object({
    targetCurrency: z.literal('EUR'),
    taxDate: ledgerDateSchema,
    rate: fiscalFxRateSchema,
    amounts: z.array(z.object({
        taxRatePct: percentSchema,
        originalNet: moneySchema,
        originalTax: moneySchema,
        fiscalNet: moneySchema,
        fiscalTax: moneySchema,
    })),
});
//# sourceMappingURL=fiscal-fx.js.map